import { l } from 'client/Other/Translation'
import * as React from 'react'

import { TotalColumnsState } from '../../../Interfaces/ColumnsSettingsInterface'
import { SchemaInterface } from '../../../Interfaces/Schema/SchemaInterface'
import { GlobalEvents } from '../../../Other/GlobalEvents'
import { TestableInterface } from '../../common/interfaces/TestableInterface'
import { ToggleInput } from '../../common/ToggleInput/ToggleInput'

declare const _

export interface ColumnsSettingsState {
  menuOpen: boolean
}

export interface ColumnsSettingsProps extends TestableInterface {
  onChange: (value) => void
  map: { [key: string]: string }
  schema: SchemaInterface
  name: string
  value: string[]
  showTotalSelection: boolean
}

export class ColumnsSettings extends React.Component<ColumnsSettingsProps, ColumnsSettingsState> {
  constructor(props: ColumnsSettingsProps) {
    super(props)
    this.state = {
      menuOpen: false,
    }
  }

  private handleOnBlur() {
    this.setState({ menuOpen: false })
  }

  private e

  public componentDidMount() {
    // if there is no selected total column, then select grossTotal.
    // To change the state, handleClickOnMultiSelect is called
    if (!this.selectedTotalColumn()) {
      this.handleClickOnMultiSelect('grossTotal')
    }
    this.e = GlobalEvents.Click((e) => this.handleOnBlur())
  }

  public componentWillUnmount() {
    GlobalEvents.unBind(this.e)
  }

  private getTotalColumnOptionsMap() {
    return {
      grossTotal: l('invoice.line.totalWithTax'),
      netTotal: l('invoice.line.netTotal'),
      bothTotals: l('invoice.line.bothTotals'),
    }
  }

  private handleValueClick(e: Event) {
    const value: string = (e.target as HTMLInputElement).getAttribute('value')
    this.handleClickOnMultiSelect(value)
    GlobalEvents.prevent(this.e)
    return false
  }

  private handleCheckBoxClick(e: Event) {
    const value: string = (e.target as HTMLInputElement).parentElement.getAttribute('value')
    return this.handleClickOnMultiSelect(value)
  }

  private handleClickOnMultiSelect(value) {
    let values = []
    if (!this.props.value) {
      values.push(value)
    } else if (_.includes(this.props.value, value)) {
      // remove clicked value from array
      values = _.difference(this.props.value, [value])
    } else {
      // add clicked value to array in its place
      const columns = Object.keys(this.props.map).concat(['netTotal', 'grossTotal'])
      columns.forEach((key) => {
        if (_.includes(this.props.value, key) || key === value) {
          values.push(key)
        }
      })
    }
    this.props.onChange(values)
  }

  private onColumnsSelectionMade(selectedColumn) {
    const selectedColumns = []
    // if bothTotals is selected, add both grossTotal and netTotal
    if (_.includes(['grossTotal', 'bothTotals'], selectedColumn)) {
      selectedColumns.push('grossTotal')
    }
    if (_.includes(['netTotal', 'bothTotals'], selectedColumn)) {
      selectedColumns.push('netTotal')
    }
    // before adding newly selected columns remove old columns selection from array
    let values = _.difference(this.props.value, ['grossTotal', 'netTotal'])
    values = values.concat(selectedColumns)
    this.props.onChange(values)
  }

  private selectedTotalColumn(): string {
    let selectedColumn
    if (_.intersection(this.props.value, ['total', 'grossTotal']).length) {
      selectedColumn = 'grossTotal'
    }
    if (_.includes(this.props.value, 'netTotal')) {
      selectedColumn = selectedColumn ? 'bothTotals' : 'netTotal'
    }
    return selectedColumn
  }

  private getSelectedValues(): string {
    const selectedValues = []
    this.props.value.forEach((value) => {
      selectedValues.push(
        this.props.map[value] || (this.props.showTotalSelection && this.getTotalColumnOptionsMap()[value]),
      )
    })
    return selectedValues.filter(Boolean).join(',')
  }

  private renderSelectMenu(): JSX.Element[] {
    const result = []
    const self = this

    Object.keys(this.props.map).forEach((key) => {
      // don't show 'total' option in here because total columns settings is seperate
      if (key === 'total') {
        return false
      }

      const value = self.props.map[key]
      let checked = false

      if (self.props.value && _.includes(self.props.value, key)) {
        checked = true
      }

      result.push(
        <div className="option" key={key} onClick={() => self.handleClickOnMultiSelect(key)} data-value={key}>
          <div className={`check ${checked ? 'checked' : ''}`}> </div>
          <label>{value}</label>
        </div>,
      )
    })
    return result
  }

  private renderTotalSelection(): JSX.Element[] {
    const result = []
    result.push(<hr />)
    result.push(
      <ToggleInput
        key="1"
        radio={true}
        label={l('invoice.visibleTotalColumns')}
        name="totalColumnsStateToggle"
        automation={this.props.automation + '-totalColumnsState'}
        value={this.selectedTotalColumn()}
        map={this.getTotalColumnOptionsMap()}
        onChange={(selectedTotalColumn: TotalColumnsState) => this.onColumnsSelectionMade(selectedTotalColumn)}
      />,
    )
    return result
  }

  public render(): JSX.Element {
    const discountContent: JSX.Element = <div />
    let toggles = null
    const classes = ['ColumnsSettings', 'Controlled', 'Input']

    const field = this.props.schema.fields.filter((field) => {
      return field.name === this.props.name
    })[0]

    if (!field) {
      return null
    }

    if (this.state.menuOpen) {
      toggles = (
        <div className="toggles">
          {this.renderSelectMenu()}
          {this.props.showTotalSelection && this.renderTotalSelection()}
        </div>
      )
      classes.push('menuOpen')
    }

    return (
      <div className="subFields">
        <div
          className={classes.join(' ')}
          data-automation={this.props.automation}
          data-value={this.props.value}
          onClick={(e) => {
            GlobalEvents.prevent(this.e)
            return false
          }}
        >
          <span
            className="Label"
            data-automation={this.props.automation + '-label'}
            onClick={() => {
              this.setState({ menuOpen: !this.state.menuOpen })
            }}
          >
            {l('invoice.Fields')}
          </span>
          <div
            className="values Controlled"
            data-automation={this.props.automation + '-value'}
            onClick={() => {
              this.setState({ menuOpen: !this.state.menuOpen })
            }}
          >
            <span className="values">{this.getSelectedValues()}</span>
            <span className="arrow" />
          </div>
          {toggles}
        </div>
      </div>
    )
  }
}
